import React, { useState, useEffect } from 'react';
import { callTasksApi, CallTask, CallTaskClaim } from '../api/callTasks';

interface CallTaskModalProps {
  task: CallTask;
  onComplete: () => void;
  onClose: () => void;
}

const CallTaskModal: React.FC<CallTaskModalProps> = ({ task, onComplete, onClose }) => {
  console.log('🎭 CallTaskModal MOUNTED with task:', task);

  const [claims, setClaims] = useState<CallTaskClaim[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedClaimId, setSelectedClaimId] = useState('');
  const [selectAllClaims, setSelectAllClaims] = useState(false);
  const [notes, setNotes] = useState('');
  const [callAnswered, setCallAnswered] = useState(true);
  const [direction, setDirection] = useState<'incoming' | 'outgoing'>('outgoing');
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadClaims = async () => {
      try {
        const response = await callTasksApi.getTaskClaims(task.id);
        setClaims(response.claims || []);

        // Auto-select if only one claim
        if (response.claims && response.claims.length === 1) {
          setSelectedClaimId(response.claims[0].id);
        }
      } catch (err: any) {
        setError(err.response?.data?.error || 'Failed to load claims');
      } finally {
        setLoading(false);
      }
    };

    loadClaims();
  }, [task.id]);

  const handleComplete = async () => {
    if (!selectAllClaims && !selectedClaimId) {
      setError('Please select a claim or choose to record against all claims');
      return;
    }

    setCompleting(true);
    setError(null);

    try {
      if (selectAllClaims) {
        // Complete task for each claim, passing expected count
        for (const claim of claims) {
          await callTasksApi.completeTask(task.id, {
            claim_id: claim.id,
            notes,
            call_answered: callAnswered,
            direction,
            expected_claims_count: claims.length,
          });
        }
      } else {
        // Single claim completion
        await callTasksApi.completeTask(task.id, {
          claim_id: selectedClaimId,
          notes,
          call_answered: callAnswered,
          direction,
        });
      }
      onComplete();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to complete task');
    } finally {
      setCompleting(false);
    }
  };

  const handleSelectAllChange = (checked: boolean) => {
    setSelectAllClaims(checked);
    if (checked) {
      setSelectedClaimId(''); // Clear individual selection when selecting all
    }
  };

  const handleIndividualClaimSelect = (claimId: string) => {
    setSelectedClaimId(claimId);
    setSelectAllClaims(false); // Uncheck select all when selecting individual claim
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds?: number) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const calculateUnits = (seconds?: number) => {
    if (!seconds) return 0;
    const minutes = seconds / 60;
    return Math.ceil(minutes / 6);
  };

  const isCompleted = task.status === 'completed';

  console.log('🎭 CallTaskModal RENDERING - showModal should be true');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-[95%] max-w-[1200px] max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex justify-between items-center px-8 py-6 border-b border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-900 m-0">
            {isCompleted ? 'View Call Task' : 'Review Call Task'}
          </h2>
          <button
            className="bg-transparent border-0 text-3xl text-gray-500 cursor-pointer p-0 w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100 hover:text-gray-700 transition-all duration-200"
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="p-8 overflow-y-auto flex-1">
          {error && (
            <div className="bg-red-100 text-red-800 border border-red-300 p-4 rounded-lg mb-6">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-[300px_1fr_400px] gap-8">
            {/* Left Column - Call Details */}
            <div className="mb-8 lg:mb-0">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Call Details</h3>
              <div className="space-y-6">
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Client</label>
                  <div className="text-base text-gray-700">
                    {task.clientName || 'Unknown'}
                    <div className="text-sm text-gray-500 mt-1">{task.clientEmail}</div>
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Phone</label>
                  <div className="text-base text-gray-700">{task.phoneNumber}</div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-gray-600 uppercase tracking-wide">User</label>
                  <div className="text-base text-gray-700">
                    {task.userFirstname && task.userLastname
                      ? `${task.userFirstname} ${task.userLastname}`
                      : 'Unknown'}
                    {task.userExtension && <div className="text-sm text-gray-500 mt-1">Ext: {task.userExtension}</div>}
                  </div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Call Date</label>
                  <div className="text-base text-gray-700">{formatDate(task.callDatetime)}</div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Duration</label>
                  <div className="text-base text-gray-700">{formatDuration(task.durationSeconds)}</div>
                </div>

                <div className="flex flex-col gap-2">
                  <label className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Units</label>
                  <div className="text-base text-gray-700">{calculateUnits(task.durationSeconds)} units</div>
                </div>
              </div>
            </div>

            {/* Middle Column - Transcription */}
            <div className="mb-8 lg:mb-0">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Transcription</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-sm leading-relaxed text-gray-700 max-h-96 overflow-y-auto whitespace-pre-wrap">
                {task.transcription || 'No transcription available'}
              </div>
            </div>

            {/* Right Column - Claims & Options */}
            <div>
              {!isCompleted ? (
                <>
                  {/* Claims Selection */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Select Claim</h3>

                    {loading ? (
                      <div className="text-center py-8 text-gray-500">Loading claims...</div>
                    ) : claims.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 italic bg-gray-50 border border-gray-200 rounded-lg">
                        No claims found for this client
                      </div>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {/* Select All Option */}
                        {claims.length > 1 && (
                          <div className="flex items-start gap-4 p-4 border-2 border-blue-300 rounded-lg cursor-pointer bg-blue-50">
                            <input
                              type="checkbox"
                              id="select-all-claims"
                              checked={selectAllClaims}
                              onChange={(e) => handleSelectAllChange(e.target.checked)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <label htmlFor="select-all-claims" className="font-semibold text-gray-700 mb-1 cursor-pointer">
                                Record against all claims
                              </label>
                              <div className="text-sm text-gray-500">
                                This call will be recorded for all {claims.length} claims
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Individual Claims */}
                        {claims.map((claim) => (
                          <div
                            key={claim.id}
                            className={`flex items-start gap-4 p-4 border-2 rounded-lg cursor-pointer transition-all duration-200 ${
                              selectedClaimId === claim.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                            }`}
                            onClick={() => handleIndividualClaimSelect(claim.id)}
                            style={{ opacity: selectAllClaims ? 0.5 : 1, pointerEvents: selectAllClaims ? 'none' : 'auto' }}
                          >
                            <input
                              type="radio"
                              name="claim"
                              checked={selectedClaimId === claim.id}
                              onChange={() => handleIndividualClaimSelect(claim.id)}
                              disabled={selectAllClaims}
                              className="mt-1 cursor-pointer"
                            />
                            <div className="flex-1">
                              <div className="font-semibold text-gray-700 mb-1">{claim.CRAClientName}</div>
                              <div className="text-sm text-gray-500">
                                ID: {claim.id} | Created: {formatDate(claim.createdInApiAt)}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Call Options */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Call Options</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="direction" className="text-sm font-medium text-gray-600 block mb-2">
                          Direction
                        </label>
                        <select
                          id="direction"
                          value={direction}
                          onChange={(e) => setDirection(e.target.value as 'incoming' | 'outgoing')}
                          className="w-full p-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="outgoing">Outgoing</option>
                          <option value="incoming">Incoming</option>
                        </select>
                      </div>

                      <div className="flex items-center">
                        <label className="text-sm font-medium text-gray-600 flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={callAnswered}
                            onChange={(e) => setCallAnswered(e.target.checked)}
                            className="w-5 h-5 cursor-pointer"
                          />
                          Call Answered
                        </label>
                      </div>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="mb-8">
                    <h3 className="text-lg font-semibold text-gray-700 mb-4">Additional Notes (Optional)</h3>
                    <textarea
                      className="w-full p-3 border border-gray-200 rounded-lg text-sm font-sans resize-y focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Add any additional notes about this call..."
                      rows={3}
                    />
                  </div>
                </>
              ) : (
                /* Completed Info */
                <div className="mb-8">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Completion Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Completed At</label>
                      <div className="text-base text-gray-700">{formatDate(task.completedAt)}</div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Completed By</label>
                      <div className="text-base text-gray-700">{task.completedBy || 'Unknown'}</div>
                    </div>
                    <div className="flex flex-col gap-2 md:col-span-2">
                      <label className="text-sm font-semibold text-gray-600 uppercase tracking-wide">
                        {task.assignedClaimIds && task.assignedClaimIds.length > 1 ? 'Assigned Claim IDs' : 'Claim ID'}
                      </label>
                      <div className="text-base text-gray-700">
                        {task.assignedClaimIds && task.assignedClaimIds.length > 0 ? (
                          task.assignedClaimIds.length > 1 ? (
                            <ul className="list-disc list-inside space-y-1">
                              {task.assignedClaimIds.map((claimId) => (
                                <li key={claimId}>{claimId}</li>
                              ))}
                            </ul>
                          ) : (
                            task.assignedClaimIds[0]
                          )
                        ) : (
                          task.claimId || 'N/A'
                        )}
                      </div>
                    </div>
                    {task.notes && (
                      <div className="flex flex-col gap-2 md:col-span-2">
                        <label className="text-sm font-semibold text-gray-600 uppercase tracking-wide">Notes</label>
                        <div className="text-base text-gray-700">{task.notes}</div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-4 px-8 py-6 border-t border-gray-200">
          <button
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200"
            onClick={onClose}
          >
            {isCompleted ? 'Close' : 'Cancel'}
          </button>
          {!isCompleted && (
            <button
              className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors duration-200 disabled:bg-gray-300 disabled:cursor-not-allowed"
              onClick={handleComplete}
              disabled={completing || (!selectAllClaims && !selectedClaimId) || loading}
            >
              {completing
                ? 'Completing...'
                : selectAllClaims
                  ? `Complete & Save to ${claims.length} Claim${claims.length > 1 ? 's' : ''}`
                  : 'Complete & Save to Claim'
              }
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallTaskModal;